import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Modal,
  message,
  Typography,
  Space,
  Input,
  Descriptions,
} from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Booking, Container, Port } from "../../types/booking";
import { AGENT_ROUTES } from "../../config/agentPortal";
import {
  deconsolidateAgentBooking,
  updateAgentContainer,
} from "../../services/agentPortalService";
import { useDebouncedSave } from "../../hooks/useDebouncedSave";
import { AgentContainerStatusTag } from "./agentStatusTags";
import { AgentAutoSaveStatus } from "./AgentAutoSaveStatus";
import { notifyAgentSaveSuccess } from "../../utils/agentAutoSaveFeedback";
import { getLinkedMastersFromContainer } from "../../utils/agentContainerMasterUtils";

const { Text, Link } = Typography;

const resolvePort = (
  port: Port | Port[] | undefined
): Port | undefined => {
  if (!port) return undefined;
  return Array.isArray(port) ? port[0] : port;
};

const formatDate = (value: string | null | undefined): string =>
  value ? dayjs(value).format("MMM D, YYYY") : "-";

interface AgentBookingContainerCardProps {
  booking: Booking;
  agentId: number;
  onUpdated: () => void;
}

export const AgentBookingContainerCard: React.FC<
  AgentBookingContainerCardProps
> = ({ booking, agentId, onUpdated }) => {
  const navigate = useNavigate();
  const [trackInput, setTrackInput] = useState("");
  const [savingTrack, setSavingTrack] = useState(false);
  const [lastSavedTrack, setLastSavedTrack] = useState<Date | null>(null);
  const scheduleSave = useDebouncedSave(600);

  const containerRaw = booking.container_info;
  const container: Container | null = containerRaw
    ? Array.isArray(containerRaw)
      ? containerRaw[0]
      : containerRaw
    : null;

  useEffect(() => {
    setTrackInput(container?.track_and_trace ?? "");
  }, [container?.track_and_trace]);

  const persistTrack = async (value: string) => {
    if (!container) return;
    const trimmed = value.trim();
    if (trimmed === (container.track_and_trace ?? "").trim()) return;

    setSavingTrack(true);
    try {
      const { error } = await updateAgentContainer(container.id, {
        track_and_trace: trimmed || null,
      });
      if (error) throw error;
      setLastSavedTrack(new Date());
      notifyAgentSaveSuccess("Track & trace", "agent-track-save");
      onUpdated();
    } catch (err) {
      console.error(err);
      message.error("Failed to save track & trace");
    } finally {
      setSavingTrack(false);
    }
  };

  const originPort = container
    ? resolvePort(
        container.container_origin_port ?? container.origin_port_info
      )
    : undefined;
  const destinationPort = container
    ? resolvePort(
        container.container_destination_port ?? container.destination_port_info
      )
    : undefined;
  const linkedMasters = container
    ? getLinkedMastersFromContainer(container)
    : [];

  const handleDeconsolidate = async () => {
    const { error } = await deconsolidateAgentBooking(agentId, booking.id);
    if (error) throw error;
    message.success("Booking removed from container");
    onUpdated();
  };

  const confirmDeconsolidate = () => {
    Modal.confirm({
      title: "Deconsolidate booking?",
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            This will remove booking{" "}
            <strong>
              {booking.reference ? `${booking.reference} (#${booking.id})` : `#${booking.id}`}
            </strong>{" "}
            from container{" "}
            <strong>{container?.reference || `#${container?.id}`}</strong>.
          </p>
          <p style={{ marginBottom: 0 }}>
            The booking will no longer be linked to this container.
          </p>
        </div>
      ),
      okText: "Deconsolidate",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await handleDeconsolidate();
        } catch (err) {
          console.error(err);
          message.error("Failed to deconsolidate");
          return Promise.reject(err);
        }
      },
    });
  };

  return (
    <Card
      size="small"
      title="Container"
      styles={{ header: { flexWrap: "wrap", gap: 8 } }}
      extra={
        container ? (
          <Space size={[6, 4]} wrap align="center" style={{ justifyContent: "flex-end" }}>
            {container.reference ? <Text strong>{container.reference}</Text> : null}
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={() => navigate(AGENT_ROUTES.containerDetail(container.id))}
            >
              Open container
            </Button>
          </Space>
        ) : null
      }
    >
      {!container ? (
        <Text type="secondary">Booking is not consolidated</Text>
      ) : (
        <>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Status">
              {container.status ? (
                <AgentContainerStatusTag status={container.status} />
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Route">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span>{originPort?.name || "-"}</span>
                <ArrowRightOutlined style={{ fontSize: 12, color: "#bfbfbf" }} />
                <span>{destinationPort?.name || "-"}</span>
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="ETD">
              {formatDate(container.delayed_etd || container.etd)}
            </Descriptions.Item>
            <Descriptions.Item label="ETA">
              {formatDate(container.eta)}
            </Descriptions.Item>
            <Descriptions.Item label="Track & trace">
              {container.track_and_trace ? (
                <a href={container.track_and_trace} target="_blank" rel="noreferrer">
                  Open link
                </a>
              ) : (
                <Input
                  size="small"
                  placeholder="Paste tracking URL"
                  value={trackInput}
                  disabled={savingTrack}
                  onChange={(e) => {
                    const next = e.target.value;
                    setTrackInput(next);
                    scheduleSave("track", () => persistTrack(next));
                  }}
                  onBlur={() => persistTrack(trackInput)}
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Master BL/AWB">
              {linkedMasters.length === 0 ? (
                <Text type="secondary">Not linked</Text>
              ) : (
                <Space direction="vertical" size={2}>
                  {linkedMasters.map((master) => {
                    const label =
                      master.reference || `Master #${master.id}`;
                    const canOpen =
                      master.agent_id != null && master.agent_id === agentId;
                    return canOpen ? (
                      <Link
                        key={master.id}
                        onClick={() =>
                          navigate(
                            `${AGENT_ROUTES.masterContainers}?master_id=${master.id}&action=manage`
                          )
                        }
                      >
                        {label}
                      </Link>
                    ) : (
                      <Text key={master.id} strong>
                        {label}
                      </Text>
                    );
                  })}
                </Space>
              )}
            </Descriptions.Item>
          </Descriptions>

          {!container.track_and_trace ? (
            <div style={{ marginTop: 8 }}>
              <AgentAutoSaveStatus
                saving={savingTrack}
                lastSaved={lastSavedTrack}
                idleText="Track & trace saves automatically"
              />
            </div>
          ) : null}

          <div style={{ marginTop: 12, textAlign: "right" }}>
            <Button danger size="small" onClick={confirmDeconsolidate}>
              Deconsolidate
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};
